import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField } from '@/components/ui/PremiumControls';
import { ReportPaywallModal } from '@/components/paywall/ReportPaywallModal';
import { Timeline } from '@/components/premium/Experience';
import {
  PremiumReportRenderer,
  ReportPaper,
  parseReportSegments,
} from '@/components/premium/ReportPaper';
import { AnalyzingOverlay } from '@/components/premium/AnalyzingOverlay';
import { useAuthStore } from '@/lib/auth.store';
import { listMyCharts } from '@/lib/chart.api';
import { fetchPaywall } from '@/lib/business.api';
import { fortuneApi, type DayunReport, type FortuneYearReport } from '@/lib/stage4.api';
import type { BaziChart, PaywallConfigView } from '@guanwei/shared';

type Tab = 'year' | 'dayun';

interface ChartItem {
  id: string;
  displayName: string;
  baziKey: string;
  gender: string;
  chartData: BaziChart;
  createdAt: string;
}

function immersiveShellClass(historyCollapsed: boolean) {
  return `mobile-immersive-shell relative mx-auto grid h-full min-h-0 w-full max-w-[1680px] overflow-hidden rounded-2xl border border-border bg-bg-1 shadow-gold lg:rounded-3xl ${
    historyCollapsed
      ? 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[0_minmax(0,1fr)]'
      : 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[360px_minmax(0,1fr)]'
  }`;
}

function immersiveHistoryClass(historyCollapsed: boolean) {
  return `mobile-immersive-history absolute inset-y-0 left-0 z-30 h-full w-[min(82vw,360px)] min-h-0 overflow-y-auto border-r border-border bg-bg p-4 shadow-gold transition-transform duration-base lg:relative lg:z-auto lg:w-auto lg:shadow-none lg:p-8 ${
    historyCollapsed
      ? '-translate-x-full lg:invisible lg:p-0 lg:opacity-0'
      : 'translate-x-0 lg:visible lg:opacity-100'
  }`;
}

function HistoryToggle({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-3 top-3 z-40 inline-flex items-center gap-2 rounded-full border border-border-accent bg-accent px-4 py-2 text-sm font-medium text-accent-fg shadow-gold transition-all hover:bg-accent-hover active:bg-accent-active lg:left-4 lg:top-4"
      aria-label={collapsed ? '查看历史记录' : '隐藏历史记录'}
    >
      <span aria-hidden="true">☰</span>
      <span>{collapsed ? '查看历史' : '隐藏历史'}</span>
    </button>
  );
}

function formatChartOptionDescription(chart: ChartItem) {
  const gender = chart.gender === 'male' ? '男' : chart.gender === 'female' ? '女' : '未指定';
  return `${gender} · ${chart.baziKey}`;
}

function statusLabel(status: string) {
  const map: Record<string, string> = {
    pending: '等待中',
    streaming: '生成中',
    completed: '已完成',
    failed: '失败',
    cast: '已成卦',
    interpreted: '已解读',
  };
  return map[status] ?? status;
}

export function FortunePage() {
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<Tab>('year');
  const [charts, setCharts] = useState<ChartItem[]>([]);
  const [chartId, setChartId] = useState('');
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [historyCollapsed, setHistoryCollapsed] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 1024,
  );
  const [year, setYear] = useState(new Date().getFullYear());
  const [dayunIndex, setDayunIndex] = useState(1);
  const [streaming, setStreaming] = useState(false);
  const [content, setContent] = useState('');
  const [reportId, setReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paywallConfig, setPaywallConfig] = useState<PaywallConfigView | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [yearHistory, setYearHistory] = useState<FortuneYearReport[]>([]);
  const [dayunHistory, setDayunHistory] = useState<DayunReport[]>([]);
  const [loadingHistoryId, setLoadingHistoryId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const scene = tab === 'year' ? 'fortune.year.preview' : 'fortune.dayun.preview';
    void fetchPaywall(scene)
      .then(setPaywallConfig)
      .catch(() => setPaywallConfig(null));
  }, [tab]);

  useEffect(() => {
    if (!user) return;
    void Promise.all([fortuneApi.listYears(), fortuneApi.listDayun()])
      .then(([years, dayuns]) => {
        setYearHistory(years);
        setDayunHistory(dayuns);
      })
      .catch(() => {
        setYearHistory([]);
        setDayunHistory([]);
      });
    void listMyCharts()
      .then((list) => {
        setCharts(list);
        if (list.length > 0) setChartId(list[0].id);
      })
      .catch(() => setCharts([]))
      .finally(() => setLoadingCharts(false));
  }, [user]);

  const selectedChart = charts.find((c) => c.id === chartId);
  const dayunOptions = selectedChart?.chartData?.daYun ?? [];
  const yearOptions = Array.from({ length: 9 }, (_, index) => new Date().getFullYear() - 2 + index);

  const start = async () => {
    if (!chartId) {
      setError('请选择命盘');
      return;
    }
    setError(null);
    setContent('');
    setReportId(null);
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;

    const handlers = {
      onDelta: (delta: string) => setContent((s) => s + delta),
      onDone: (id?: string) => {
        setStreaming(false);
        if (id) setReportId(id);
        void Promise.all([fortuneApi.listYears(), fortuneApi.listDayun()])
          .then(([years, dayuns]) => {
            setYearHistory(years);
            setDayunHistory(dayuns);
          })
          .catch(() => undefined);
      },
      onError: (err: Error) => {
        if (err.message.includes('PAYWALL_REQUIRED')) {
          setPaywallOpen(true);
          setError('该报告需要购买或使用权益后生成');
        } else {
          setError(err.message);
        }
        setStreaming(false);
      },
    };

    if (tab === 'year') {
      await fortuneApi.yearStream({ chartId, targetYear: year }, handlers, controller.signal);
    } else {
      await fortuneApi.dayunStream({ chartId, dayunIndex }, handlers, controller.signal);
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  const openHistory = async (kind: Tab, id: string) => {
    setLoadingHistoryId(id);
    setError(null);
    try {
      if (kind === 'year') {
        const detail = await fortuneApi.yearDetail(id);
        setTab('year');
        setChartId(detail.chartId);
        setYear(detail.targetYear);
        setContent(detail.content ?? '');
        setReportId(detail.id);
      } else {
        const detail = await fortuneApi.dayunDetail(id);
        setTab('dayun');
        setChartId(detail.chartId);
        setDayunIndex(detail.dayunIndex);
        setContent(detail.content ?? '');
        setReportId(detail.id);
      }
      setStreaming(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingHistoryId(null);
    }
  };

  const shellClass = useMemo(() => immersiveShellClass(historyCollapsed), [historyCollapsed]);
  const historyClass = useMemo(() => immersiveHistoryClass(historyCollapsed), [historyCollapsed]);

  const historyPanel = (
    <aside className={historyClass}>
      <div className="mb-5">
        <p className="text-xs uppercase tracking-[0.28em] text-accent">History</p>
        <h2 className="mt-2 font-serif text-2xl text-fg">历史报告</h2>
      </div>
      <div className="space-y-5">
        <div>
          <p className="mb-3 text-sm font-medium text-fg">流年</p>
          <div className="space-y-3">
            {yearHistory.length === 0 ? (
              <p className="text-sm text-fg-muted">暂无流年报告</p>
            ) : (
              yearHistory.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => void openHistory('year', item.id)}
                  className="w-full rounded-2xl border border-border bg-bg-1 p-4 text-left hover:border-accent hover:bg-bg-2"
                >
                  <p className="font-serif text-base text-fg">{item.targetYear} 年流年</p>
                  <p className="mt-1 text-xs text-fg-muted">
                    {loadingHistoryId === item.id ? '读取中' : statusLabel(item.status)}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
        <div>
          <p className="mb-3 text-sm font-medium text-fg">大运</p>
          <div className="space-y-3">
            {dayunHistory.length === 0 ? (
              <p className="text-sm text-fg-muted">暂无大运报告</p>
            ) : (
              dayunHistory.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => void openHistory('dayun', item.id)}
                  className="w-full rounded-2xl border border-border bg-bg-1 p-4 text-left hover:border-accent hover:bg-bg-2"
                >
                  <p className="font-serif text-base text-fg">{item.ganZhi} 大运</p>
                  <p className="mt-1 text-xs text-fg-muted">
                    {item.startYear} 起 ·{' '}
                    {loadingHistoryId === item.id ? '读取中' : statusLabel(item.status)}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </aside>
  );

  if (!user) {
    return (
      <main className="flex-1 flex items-center justify-center px-6">
        <Card className="p-10 text-center max-w-md">
          <p className="font-serif text-2xl text-fg mb-3">流年 / 大运深度报告</p>
          <p className="text-sm text-fg-2 mb-6">登录后获取战略级时间窗口推演</p>
          <Link to="/auth/login">
            <Button size="lg">登录</Button>
          </Link>
        </Card>
      </main>
    );
  }

  if (loadingCharts) {
    return (
      <main className="flex-1 flex items-center justify-center px-6 text-sm text-fg-muted">
        正在读取命盘…
      </main>
    );
  }

  if (charts.length === 0) {
    return (
      <main className="flex-1 flex items-center justify-center px-6">
        <Card className="max-w-md p-10 text-center">
          <p className="mb-3 font-serif text-2xl text-fg">流年 / 大运深度报告</p>
          <p className="mb-6 text-sm leading-7 text-fg-2">
            流年和大运必须基于一张已保存命盘生成，请先完成排盘。
          </p>
          <Link to="/chart">
            <Button size="lg">先去排盘</Button>
          </Link>
        </Card>
      </main>
    );
  }

  return (
    <main className="mobile-immersive-page h-full min-h-0 overflow-hidden px-3 py-3 sm:px-6 lg:px-8 lg:py-6">
      <div className={shellClass}>
        {historyPanel}
        <section className="mobile-immersive-content relative order-2 h-full min-h-0 space-y-6 overflow-y-auto overscroll-contain bg-bg-1 p-4 sm:p-8 lg:space-y-8 lg:p-10">
          <HistoryToggle
            collapsed={historyCollapsed}
            onClick={() => setHistoryCollapsed((value) => !value)}
          />
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-serif text-3xl text-fg">时间窗口推演</h1>
            <p className="text-sm text-fg-muted mt-2">
              流年 = 一年颗粒度行动表；大运 = 十年战略主旋律
            </p>
          </motion.div>

          <div className="flex gap-2 border-b border-border">
            {(['year', 'dayun'] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTab(t);
                  setContent('');
                  setReportId(null);
                }}
                className={
                  'px-4 py-2 text-sm transition-colors duration-fast border-b-2 -mb-px ' +
                  (tab === t
                    ? 'border-accent text-accent'
                    : 'border-transparent text-fg-2 hover:text-fg')
                }
              >
                {t === 'year' ? '流年深度' : '大运 10 年'}
              </button>
            ))}
          </div>

          <Card className="p-6 space-y-5">
            <SelectField
              label="选择命盘"
              value={chartId}
              onChange={setChartId}
              placeholder="选择命盘"
              options={charts.map((c) => ({
                value: c.id,
                label: c.displayName,
                description: formatChartOptionDescription(c),
              }))}
            />

            {tab === 'year' ? (
              <div>
                <p className="mb-2 text-sm text-fg-1">选择流年</p>
                <Timeline
                  items={yearOptions.map((y) => ({
                    title: String(y),
                    subtitle: y === new Date().getFullYear() ? '今年' : '年度窗口',
                  }))}
                  activeIndex={Math.max(0, yearOptions.indexOf(year))}
                  onSelect={(index) => setYear(yearOptions[index])}
                />
              </div>
            ) : (
              <div>
                <p className="mb-2 text-sm text-fg-1">选择大运</p>
                <Timeline
                  items={
                    dayunOptions.length
                      ? dayunOptions.slice(0, 8).map((d, i) => ({
                          title: `第 ${i + 1} 步`,
                          subtitle: `${d.ganZhi} · ${d.startYear} 起`,
                        }))
                      : [{ title: '待选择', subtitle: '请先选择命盘' }]
                  }
                  activeIndex={Math.max(0, dayunIndex - 1)}
                  onSelect={(index) => setDayunIndex(index + 1)}
                />
              </div>
            )}

            {error && <p className="text-sm text-danger">{error}</p>}

            <div className="flex gap-3">
              {!streaming ? (
                <Button onClick={start} size="lg">
                  {tab === 'year' ? `生成 ${year} 年深度报告` : '生成大运十年规划'}
                </Button>
              ) : (
                <Button variant="outline" onClick={stop}>
                  中止
                </Button>
              )}
            </div>
          </Card>

          {(streaming || content) && (
            <ReportPaper
              title={tab === 'year' ? `${year} 年流年报告` : '大运 10 年战略'}
              subtitle="把时间窗口拆成现实决策、风险回避与行动节奏。"
            >
              {streaming && !content ? (
                <AnalyzingOverlay />
              ) : (
                <PremiumReportRenderer
                  segments={parseReportSegments(content)}
                  streaming={streaming}
                />
              )}
              {reportId && <p className="text-xs text-fg-muted mt-6">报告 ID：{reportId}</p>}
            </ReportPaper>
          )}
        </section>
      </div>
      <ReportPaywallModal
        open={paywallOpen}
        scene={tab === 'year' ? 'fortune.year.preview' : 'fortune.dayun.preview'}
        config={paywallConfig}
        fulfillmentMeta={
          tab === 'year'
            ? { chartId, targetYear: String(year) }
            : { chartId, dayunIndex: String(dayunIndex) }
        }
        onClose={() => setPaywallOpen(false)}
        dismissText="我再考虑一下"
      />
    </main>
  );
}
